import { useCallback, useRef, useState } from "react";
import { useDemo } from "../zustand/DemoZustand";
import { useChat } from "../zustand/ChatZustand";
import { Haken, Kreuz, PfeilRechts, Schloss, Sprechblase, Uhr } from "./Symbole";
import { gehoertZuMir, kuerzel, nameVon } from "../hilfen/auswertung";
import { fristInWorten, kurzesDatum, tageBis } from "../hilfen/datum";

/*
  Das Aufgabenbrett.

  Spalten legt die Fraktion selbst an — „Offen“, „In Arbeit“, „Erledigt“ sind
  nur der Anfangsbestand. Ein Satz Spalten gilt für beide Bretter: Hieße
  „In Arbeit“ für jeden etwas anderes, wäre das Fraktionsboard kein
  Gruppenspiegel mehr, sondern eine Sammlung privater Ordnungen.

  ---------------------------------------------------------------
  Verschieben
  ---------------------------------------------------------------

  Karten werden GEZOGEN, mit dem Finger wie mit der Maus. Das kehrt eine
  frühere Entscheidung um, die Ziehen auf dem Tablet für zu ungenau hielt.

  Zwei Dinge machen es benutzbar:

    Ziehen beginnt erst nach kurzem Halten. Sonst nähme jede Wischbewegung
    eine Karte mit, und das Brett ließe sich nicht mehr seitlich schieben.

    Die Zielspalte hebt sich hervor, solange die Karte darüber schwebt.
    Ohne Rückmeldung weiß niemand, wo er gerade ist.

  Der Knopfweg bleibt daneben bestehen: Antippen öffnet die Karte, dort
  lässt sich die Spalte wählen; „→“ schiebt eine Spalte weiter. Das ist
  keine Doppelung aus Unsicherheit — die Zielgruppe reicht bis 80 Jahre, und
  wer zittrige Hände hat oder mit der Tastatur arbeitet, muss eine Karte
  trotzdem bewegen können.
*/

/* Wie lange gehalten werden muss, bevor eine Karte sich löst. */
const HALTEN_MS = 250;

export default function Aufgabenbrett({ brett = "fraktion", aufKarte }) {
  const { zustand, stammdaten, ich, aktionen } = useDemo();
  const spalten = [...zustand.spalten].sort((a, b) => a.reihenfolge - b.reihenfolge);

  /*
    Dein Board zeigt, was DICH angeht: wofür du verantwortlich bist und was
    du beobachtest.

    Auch eine Aufgabe, die man weitergegeben hat, bleibt damit im eigenen
    Brett stehen — wer anlegt, beobachtet von selbst. Wer beim Bauamt
    nachfragen lässt, will wissen, ob die Antwort da ist; sie aus den Augen
    zu verlieren, nur weil ein anderer sie erledigt, wäre das Gegenteil von
    Übersicht. Wer sie macht, steht als „Bei …“ auf der Karte.

    Das Fraktionsboard zeigt, was mehrere betrifft. Private Aufgaben anderer
    kommen ohnehin nicht vom Server.
  */
  const aufgaben = zustand.aufgaben.filter((a) =>
    brett === "meins" ? gehoertZuMir(a, ich.id) : a.sichtbarkeit === "fraktion"
  );

  const [gezogen, setzeGezogen] = useState(null);
  const [ueber, setzeUeber] = useState(null);

  /* Die Spaltenkästen, um beim Ziehen zu wissen, worüber der Finger steht. */
  const kaesten = useRef({});

  const spalteUnter = useCallback((x, y) => {
    for (const [id, kasten] of Object.entries(kaesten.current)) {
      if (!kasten) continue;
      const r = kasten.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return id;
    }
    return null;
  }, []);

  async function ablegen(aufgabe, spalteId) {
    if (!spalteId || spalteId === aufgabe.spalteId) return;
    await aktionen.aendereAufgabe(aufgabe.id, { spalteId });
  }

  async function eineWeiter(aufgabe) {
    const jetzt = spalten.findIndex((s) => s.id === aufgabe.spalteId);
    const naechste = spalten[(jetzt + 1) % spalten.length];
    if (naechste) await aktionen.aendereAufgabe(aufgabe.id, { spalteId: naechste.id });
  }

  return (
    <div className="brett">
      {spalten.map((spalte, stelle) => {
        const drin = aufgaben
          .filter((a) => {
            if (a.spalteId === spalte.id) return true;
            /*
              Karten, deren Spalte es nicht mehr gibt, landen in der ersten.

              Beim Entfernen einer Spalte hängt die Oberfläche um, was sie
              sieht — die privaten Aufgaben der anderen sieht sie nicht. Ohne
              diesen Auffang stünde eine solche Karte in keiner Spalte und
              wäre für ihren Besitzer verschwunden, obwohl sie noch da ist.
            */
            return stelle === 0 && !spalten.some((s) => s.id === a.spalteId);
          })
          .sort((a, b) => a.reihenfolge - b.reihenfolge);

        return (
          <div
            key={spalte.id}
            ref={(el) => (kaesten.current[spalte.id] = el)}
            className={`brett__spalte ${ueber === spalte.id ? "brett__spalte--ziel" : ""}`}
          >
            <Spaltenkopf spalte={spalte} anzahl={drin.length} />

            {drin.length === 0 && <p className="brett__leer">Nichts hier.</p>}

            {drin.map((aufgabe) => (
              <Karte
                key={aufgabe.id}
                aufgabe={aufgabe}
                spalten={spalten}
                stammdaten={stammdaten}
                ichId={ich.id}
                gezogen={gezogen === aufgabe.id}
                aufOeffnen={() => aufKarte(aufgabe.id)}
                aufWeiter={() => eineWeiter(aufgabe)}
                aufZiehen={{
                  start: () => setzeGezogen(aufgabe.id),
                  ueber: (x, y) => setzeUeber(spalteUnter(x, y)),
                  ende: (x, y) => {
                    const ziel = spalteUnter(x, y);
                    setzeGezogen(null);
                    setzeUeber(null);
                    ablegen(aufgabe, ziel);
                  },
                  abbrechen: () => {
                    setzeGezogen(null);
                    setzeUeber(null);
                  },
                }}
              />
            ))}

            <NeueKarte spalteId={spalte.id} />
          </div>
        );
      })}

      <NeueSpalte />
    </div>
  );
}

/* ---------------------------------------------------------------
   Die Karte
   --------------------------------------------------------------- */

function Karte({ aufgabe, spalten, stammdaten, ichId, gezogen, aufOeffnen, aufWeiter, aufZiehen }) {
  const chat = useChat();
  const halter = useRef(null);
  const zieht = useRef(false);
  const bewegt = useRef(false);
  const start = useRef(null);
  const [versatz, setzeVersatz] = useState(null);

  const dringend = !aufgabe.erledigt && tageBis(aufgabe.faellig) <= 1;
  const gesamt = (aufgabe.checkliste || []).length;
  const fertig = (aufgabe.checkliste || []).filter((p) => p.erledigt).length;
  const beitraege = chat.nachrichten.filter((n) => n.aufgabeId === aufgabe.id).length;
  const jetzt = spalten.findIndex((s) => s.id === aufgabe.spalteId);
  const naechste = spalten[(jetzt + 1) % spalten.length];
  const wer = nameVon(stammdaten, aufgabe.zustaendigId, ichId);

  function beiZeiger(e) {
    /* „→ eine Spalte weiter“ ist ein Knopf, keine Handhabe zum Ziehen. */
    if (e.target.closest(".karte-aufgabe__weiter")) return;

    /*
      Erst halten, dann ziehen. Ohne diese Schwelle wäre das Brett auf dem
      iPad nicht mehr seitlich zu schieben.
    */
    const kasten = e.currentTarget;
    const zeiger = e.pointerId;
    start.current = { x: e.clientX, y: e.clientY };
    bewegt.current = false;

    halter.current = setTimeout(() => {
      zieht.current = true;
      aufZiehen.start();
      setzeVersatz({ x: 0, y: 0 });

      /*
        Erst JETZT den Zeiger einfangen, damit die Karte auch außerhalb ihrer
        eigenen Fläche weiterverfolgt wird.

        Nicht schon beim Aufsetzen: Ein eingefangener Zeiger leitet auch das
        Klick-Ereignis auf dieses Kästchen um, und der Knopf darin bekäme es
        nie. Genau das hatte den ganzen Knopfweg unbrauchbar gemacht —
        Antippen öffnete die Karte nicht mehr.
      */
      try {
        kasten.setPointerCapture(zeiger);
      } catch (fehler) {
        /* Zeiger schon losgelassen — dann gibt es nichts einzufangen. */
      }
    }, HALTEN_MS);
  }

  function beiBewegung(e) {
    if (!start.current) return;

    const weit =
      Math.abs(e.clientX - start.current.x) > 8 || Math.abs(e.clientY - start.current.y) > 8;

    if (!zieht.current) {
      /* Wer wischt statt zu halten, will scrollen — Halten abbrechen. */
      if (weit) {
        clearTimeout(halter.current);
        start.current = null;
      }
      return;
    }

    if (weit) bewegt.current = true;
    setzeVersatz({ x: e.clientX - start.current.x, y: e.clientY - start.current.y });
    aufZiehen.ueber(e.clientX, e.clientY);
  }

  function beiEnde(e) {
    clearTimeout(halter.current);
    const wurdeGezogen = zieht.current;
    const wurdeBewegt = bewegt.current;
    zieht.current = false;
    bewegt.current = false;
    start.current = null;
    setzeVersatz(null);

    if (!wurdeGezogen) return;

    /*
      Gehalten, aber nicht bewegt — das war ein langsamer Tipp, kein Ziehen.
      Wer 80 ist, drückt selten unter einer viertel Sekunde wieder los. Das
      Klick-Ereignis fällt hier wegen des eingefangenen Zeigers aus, also
      öffnen wir die Karte von Hand.
    */
    if (!wurdeBewegt) {
      aufZiehen.abbrechen();
      aufOeffnen();
      return;
    }

    aufZiehen.ende(e.clientX, e.clientY);
  }

  return (
    <div
      className={`karte-aufgabe ${gezogen ? "karte-aufgabe--gezogen" : ""}`}
      style={versatz ? { transform: `translate(${versatz.x}px, ${versatz.y}px)` } : undefined}
      onPointerDown={beiZeiger}
      onPointerMove={beiBewegung}
      onPointerUp={beiEnde}
      onPointerCancel={beiEnde}
    >
      <button className="karte-aufgabe__flaeche" onClick={aufOeffnen}>
        <span className="karte-aufgabe__kopf">
          <span className="karte-aufgabe__titel">{aufgabe.titel}</span>
          <span className="karte-aufgabe__wer" title={wer}>
            {kuerzel(wer)}
          </span>
        </span>

        {(aufgabe.etiketten || []).length > 0 && (
          <span className="karte-aufgabe__etiketten">
            {aufgabe.etiketten.map((e) => (
              <span key={e} className="etikett">
                {e}
              </span>
            ))}
          </span>
        )}

        <span className="karte-aufgabe__zeilen">
          <span
            className={`karte-aufgabe__fuss ${dringend ? "karte-aufgabe__fuss--dringend" : ""}`}
          >
            <Uhr groesse={13} />
            {kurzesDatum(aufgabe.faellig)} · {fristInWorten(aufgabe.faellig)}
          </span>

          {gesamt > 0 && (
            <span className="karte-aufgabe__fuss">
              <Haken groesse={13} />
              {fertig} von {gesamt}
            </span>
          )}

          {beitraege > 0 && (
            <span className="karte-aufgabe__fuss">
              <Sprechblase groesse={13} />
              {beitraege}
            </span>
          )}

          {aufgabe.sichtbarkeit === "privat" && (
            <span className="karte-aufgabe__fuss">
              <Schloss groesse={13} />
              Nur für dich
            </span>
          )}

          {/*
            Im eigenen Brett steht auch, was man weggegeben hat. Ohne diesen
            Satz sähe die Karte aus wie eigene Arbeit — die zwei Buchstaben
            im Kreis allein sind dafür zu leise.
          */}
          {wer !== "Du" && (
            <span className="karte-aufgabe__fuss">
              <PfeilRechts groesse={13} />
              Bei {wer}
            </span>
          )}
        </span>
      </button>

      {naechste && (
        <button
          className="karte-aufgabe__weiter"
          onClick={aufWeiter}
          title={`Verschiebt die Aufgabe nach „${naechste.name}“`}
        >
          <PfeilRechts groesse={14} />
          {naechste.name}
        </button>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------
   Spalten anlegen, umbenennen, entfernen
   --------------------------------------------------------------- */

function Spaltenkopf({ spalte, anzahl }) {
  const { aktionen } = useDemo();
  const [aendern, setzeAendern] = useState(false);
  const [name, setzeName] = useState(spalte.name);

  if (aendern) {
    return (
      <div className="brett__kopf brett__kopf--aendern">
        <input
          className="brett__feld"
          value={name}
          onChange={(e) => setzeName(e.target.value)}
          aria-label="Name der Spalte"
        />
        <div className="leiste__knoepfe">
          <button
            className="knopf knopf--schlicht"
            onClick={async () => {
              if (name.trim()) await aktionen.aendereSpalte(spalte.id, { name: name.trim() });
              setzeAendern(false);
            }}
          >
            Sichern
          </button>
          <button
            className="knopf knopf--schlicht"
            onClick={() => aktionen.aendereSpalte(spalte.id, { erledigt: !spalte.erledigt })}
          >
            {spalte.erledigt ? "Gilt als erledigt ✓" : "Als „erledigt“ zählen"}
          </button>
          <button
            className="knopf knopf--schlicht"
            onClick={() => aktionen.loescheSpalte(spalte.id)}
          >
            Entfernen
          </button>
        </div>
        <p className="klein leise">
          Karten gehen beim Entfernen nicht verloren — sie wandern nach vorn.
        </p>
      </div>
    );
  }

  return (
    <button className="brett__kopf" onClick={() => setzeAendern(true)}>
      <span className="brett__name">
        {spalte.name}
        {spalte.erledigt && <Haken groesse={14} />}
      </span>
      <span className="brett__anzahl">{anzahl}</span>
    </button>
  );
}

function NeueKarte({ spalteId }) {
  const { aktionen } = useDemo();
  const [offen, setzeOffen] = useState(false);
  const [titel, setzeTitel] = useState("");

  async function anlegen() {
    if (!titel.trim()) return;
    await aktionen.fuegeAufgabeHinzu({ titel: titel.trim(), spalteId });
    setzeTitel("");
    setzeOffen(false);
  }

  if (!offen) {
    return (
      <button className="brett__plus" onClick={() => setzeOffen(true)}>
        + Aufgabe
      </button>
    );
  }

  return (
    <div className="brett__neu">
      <input
        className="brett__feld"
        autoFocus
        placeholder="Was ist zu tun?"
        value={titel}
        onChange={(e) => setzeTitel(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && anlegen()}
        aria-label="Titel der neuen Aufgabe"
      />
      <div className="leiste__knoepfe">
        <button className="knopf" onClick={anlegen} disabled={!titel.trim()}>
          Anlegen
        </button>
        <button className="knopf knopf--schlicht" onClick={() => setzeOffen(false)}>
          <Kreuz groesse={16} />
        </button>
      </div>
    </div>
  );
}

function NeueSpalte() {
  const { aktionen } = useDemo();
  const [offen, setzeOffen] = useState(false);
  const [name, setzeName] = useState("");

  async function anlegen() {
    if (!name.trim()) return;
    await aktionen.fuegeSpalteHinzu(name.trim());
    setzeName("");
    setzeOffen(false);
  }

  return (
    <div className="brett__spalte brett__spalte--neu">
      {!offen ? (
        <button className="brett__plus brett__plus--gross" onClick={() => setzeOffen(true)}>
          + Neue Spalte
        </button>
      ) : (
        <div className="brett__neu">
          <input
            className="brett__feld"
            autoFocus
            placeholder="Name der Spalte"
            value={name}
            onChange={(e) => setzeName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && anlegen()}
            aria-label="Name der neuen Spalte"
          />
          <div className="leiste__knoepfe">
            <button className="knopf" onClick={anlegen} disabled={!name.trim()}>
              Anlegen
            </button>
            <button className="knopf knopf--schlicht" onClick={() => setzeOffen(false)}>
              <Kreuz groesse={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
